/**
 * SystemStatusBanner - Shows warning when system is degraded
 * Checks health endpoint and displays banner if database is unavailable
 */

import { useState, useEffect } from 'react'
import { AlertTriangle, X, RefreshCw } from 'lucide-react'

export default function SystemStatusBanner() {
  const [status, setStatus] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const [checking, setChecking] = useState(false)

  const checkHealth = async () => {
    setChecking(true)
    try {
      const response = await fetch('/health')
      const data = await response.json()
      setStatus(data)

      // If status recovered, reset dismissed state
      if (data.status === 'healthy') {
        setDismissed(false)
      }
    } catch (error) {
      setStatus({ status: 'error', error: 'Cannot reach server' })
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => {
    // Initial check
    checkHealth()

    // Check every 30 seconds
    const interval = setInterval(checkHealth, 30000)

    return () => clearInterval(interval)
  }, [])

  // Don't show anything if healthy or dismissed
  if (!status || status.status === 'healthy' || dismissed) {
    return null
  }

  const isDegraded = status.status === 'degraded'
  const isError = status.status === 'error'

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-3 ${
      isError ? 'bg-red-600' : 'bg-amber-500'
    } text-white shadow-lg`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className="flex-shrink-0" />
          <div>
            <span className="font-medium">
              {isError ? 'Connection Error' : 'Limited Functionality'}
            </span>
            <span className="hidden sm:inline ml-2 text-white/90">
              {isError
                ? 'Unable to connect to Trendy server. Please check your connection.'
                : 'Database temporarily unavailable. Your conversations may not be saved.'
              }
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={checkHealth}
            disabled={checking}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title="Check again"
          >
            <RefreshCw size={16} className={checking ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
